import React, { useState } from 'react';
import Button from './Button';
import {
  IconPencil,
  IconX,
  IconCheck,
  IconChecks,
  IconExclamationMark,
  IconFlag2Filled,
  IconInfoCircle,
} from '@tabler/icons-react';
import api from '../services/api';
import {
  parseDateInput,
  isoToDisplay,
  todayDisplay,
  DateInputField,
  parseTimeInput,
  storageToTimeDisplay,
} from '../utils/dateInput';
import {
  evaluateBP,
  evaluateBMI,
  isBmiFlagRelevant,
  BP_STATUS,
  BMI_STATUS,
  BP_EXPLANATION,
  BP_TOOLTIP,
  BMI_EXPLANATION,
} from '../utils/vitalsIndicators';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TEAL = 'var(--color-primary)';
const CHARCOAL = 'var(--color-text-primary)';
const MUTED = 'var(--color-text-muted)';
const BORDER = 'var(--color-border)';
const SUBNAV_BG = 'var(--color-subNav)';
const SUBTLE = 'var(--color-bg-subtle)';
const NAVY = 'var(--color-accent-navy)';

// Indicator colors. Green = at/below target; amber/red = transplant BMI tiers;
// red exclamation = BP outside safe range.
const GREEN = 'var(--color-primary)';
const AMBER = 'var(--color-severity-mild)';
const RED = 'var(--color-severity-high)';

// ---------------------------------------------------------------------------
// Unit conversion helpers
// ---------------------------------------------------------------------------

function kgToLbs(kg) {
  if (kg == null) return '';
  return Math.round(parseFloat(kg) * 2.20462 * 10) / 10;
}

function lbsToKg(lbs) {
  if (lbs === '' || lbs == null) return null;
  return Math.round(parseFloat(lbs) / 2.20462 * 100) / 100;
}

function cmToFtIn(cm) {
  if (cm == null) return { ft: '', in: '' };
  const totalInches = parseFloat(cm) / 2.54;
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round((totalInches % 12) * 10) / 10;
  return { ft: String(ft), in: String(inches) };
}

function ftInToCm(ft, inches) {
  if (ft === '' && inches === '') return null;
  const f = parseFloat(ft) || 0;
  const i = parseFloat(inches) || 0;
  return Math.round((f * 30.48 + i * 2.54) * 10) / 10;
}

function cmToDisplay(cm) {
  if (cm == null) return '—';
  const { ft, in: inches } = cmToFtIn(cm);
  return `${ft}' ${inches}"`;
}

function cmToDisplayMetric(cm) {
  if (cm == null) return '—';
  return `${parseFloat(cm)} cm`;
}

function kgToDisplay(kg) {
  if (kg == null) return '—';
  return `${kgToLbs(kg)} lbs`;
}

function kgToDisplayMetric(kg) {
  if (kg == null) return '—';
  return `${parseFloat(kg)} kg`;
}

function formatBP(sbp, dbp) {
  if (sbp == null || dbp == null) return '—';
  return `${sbp}/${dbp}`;
}

function formatBMI(bmi) {
  if (bmi == null) return '—';
  return parseFloat(bmi).toFixed(1);
}

// ---------------------------------------------------------------------------
// Indicator glyph renderers — map a status code to its icon. Returned inline
// so they sit next to the reading. Wrapped in a title-bearing span for a
// native tooltip on hover (legend carries the richer InfoCircle tooltips).
// ---------------------------------------------------------------------------

function BPIndicator({ sbp, dbp }) {
  const status = evaluateBP(sbp, dbp);
  if (status === BP_STATUS.NONE) return null;

  const title = BP_EXPLANATION[status]
    + (BP_TOOLTIP[status] ? ` — ${BP_TOOLTIP[status]}` : '');

  let icon = null;
  if (status === BP_STATUS.CRITICAL) {
    icon = <IconExclamationMark size={15} stroke={2.5} style={{ color: RED }} />;
  } else if (status === BP_STATUS.INTENSIVE) {
    icon = <IconChecks size={15} stroke={2} style={{ color: GREEN }} />;
  } else if (status === BP_STATUS.TARGET) {
    icon = <IconCheck size={15} stroke={2} style={{ color: GREEN }} />;
  }

  return (
    <span style={indicatorStyles.glyph} title={title} aria-label={title}>
      {icon}
    </span>
  );
}

function BMIIndicator({ bmi, patient }) {
  if (!isBmiFlagRelevant(patient)) return null;
  const status = evaluateBMI(bmi);
  if (status === BMI_STATUS.NONE) return null;

  const color = status === BMI_STATUS.RED ? RED : AMBER;
  const title = BMI_EXPLANATION[status];

  return (
    <span style={indicatorStyles.glyph} title={title} aria-label={title}>
      <IconFlag2Filled size={14} style={{ color }} />
    </span>
  );
}

const indicatorStyles = {
  glyph: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '5px',
    verticalAlign: 'middle',
  },
};

// ---------------------------------------------------------------------------
// Date/time display helpers
// ---------------------------------------------------------------------------

function formatDateDisplay(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function formatDateTimeDisplay(dateStr, timeStr) {
  const datePart = formatDateDisplay(dateStr);
  if (!timeStr) return datePart;
  return `${datePart} ${storageToTimeDisplay(timeStr)}`;
}

// ---------------------------------------------------------------------------
// Empty form
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  recorded_date: todayDisplay(),
  recorded_time: '',
  bp: '',
  hr: '',
  weight_lbs: '',
  height_ft: '',
  height_in: '',
  weight_kg: '',
  height_cm: '',
};

// ---------------------------------------------------------------------------
// Toggle switch component
// ---------------------------------------------------------------------------

function ToggleSwitch({ checked, onChange, labelLeft, labelRight }) {
  return (
    <div style={ts.wrapper}>
      <span style={{ ...ts.label, color: !checked ? TEAL : MUTED }}>{labelLeft}</span>
      <div
        style={{ ...ts.track, backgroundColor: checked ? TEAL : '#CBD5E1' }}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onChange(!checked)}
      >
        <div style={{
          ...ts.thumb,
          transform: checked ? 'translateX(18px)' : 'translateX(2px)',
        }} />
      </div>
      <span style={{ ...ts.label, color: checked ? TEAL : MUTED }}>{labelRight}</span>
    </div>
  );
}

const ts = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  track: {
    width: '40px',
    height: '22px',
    borderRadius: '11px',
    position: 'relative',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    flexShrink: 0,
  },
  thumb: {
    position: 'absolute',
    top: '3px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s ease',
  },
  label: {
    fontSize: '12px',
    fontFamily: 'var(--font-family-base)',
    fontWeight: 500,
    transition: 'color 0.2s ease',
  },
};

// ---------------------------------------------------------------------------
// Legend — always shows all four indicator meanings as a static reference.
// The KDIGO and both BMI rows carry an InfoCircle hover tooltip for the
// longer qualifier text.
// ---------------------------------------------------------------------------

function InfoTip({ text }) {
  return (
    <span style={lg.infoWrap} title={text} aria-label={text}>
      <IconInfoCircle size={13} stroke={1.75} style={{ color: 'var(--color-accent-navy)' }} />
    </span>
  );
}

function VitalsLegend() {
  return (
    <div style={lg.row}>
      <span style={lg.item}>
        <IconCheck size={15} stroke={2} style={{ color: GREEN }} />
        <span style={lg.text}>{BP_EXPLANATION[BP_STATUS.TARGET]}</span>
      </span>
      <span style={lg.item}>
        <IconChecks size={15} stroke={2} style={{ color: GREEN }} />
        <span style={lg.text}>{BP_EXPLANATION[BP_STATUS.INTENSIVE]}</span>
        <InfoTip text={BP_TOOLTIP[BP_STATUS.INTENSIVE]} />
      </span>
      <span style={lg.item}>
        <IconFlag2Filled size={14} style={{ color: AMBER }} />
        <span style={lg.text}>BMI 35–40</span>
        <InfoTip text={BMI_EXPLANATION[BMI_STATUS.AMBER]} />
      </span>
      <span style={lg.item}>
        <IconFlag2Filled size={14} style={{ color: RED }} />
        <span style={lg.text}>BMI ≥ 40</span>
        <InfoTip text={BMI_EXPLANATION[BMI_STATUS.RED]} />
      </span>
    </div>
  );
}

const lg = {
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px 18px',
    maxWidth: '740px',
    padding: '0 16px 0 16px',
    flexShrink: 0,
  },
  item: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },
  text: {
    fontSize: '11px',
    color: 'var(--color-text-secondary)',
    fontFamily: 'var(--font-family-base)',
  },
  infoWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help',
  },
};

// ---------------------------------------------------------------------------
// VitalsSection
// ---------------------------------------------------------------------------

export default function VitalsSection({ patient, patientId, resolveUser, vitals, onVitalsSaved }) {
  const [metricMode, setMetricMode] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState({});
  const [hoveredRow, setHoveredRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  // Fall back to a patientId-only shape if the parent hasn't passed the full
  // patient yet — BMI gating reads from it but tolerates undefined in Phase 1.
  const resolvedId = patientId ?? patient?.id;

  const lastHeightEntry = vitals.find(v => v.height_cm != null);

  // ── Form field change ──
  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  };

  // ── Parse BP ──
  const parseBP = (bpStr) => {
    if (!bpStr || !bpStr.trim()) return { sbp: null, dbp: null };
    const match = bpStr.trim().match(/^(\d{1,3})\s*\/\s*(\d{1,3})$/);
    if (!match) return null;
    return { sbp: parseInt(match[1]), dbp: parseInt(match[2]) };
  };

  // ── Validate ──
  const validate = () => {
    const errs = {};

    if (!form.recorded_date || !form.recorded_date.trim()) {
      errs.recorded_date = 'Date is required';
    } else if (!parseDateInput(form.recorded_date)) {
      errs.recorded_date = 'Enter as M/D/YY or M/D/YYYY';
    }

    if (form.recorded_time && form.recorded_time.trim()) {
      if (!parseTimeInput(form.recorded_time)) {
        errs.recorded_time = 'Enter as H:MM AM/PM or HH:MM';
      }
    }

    if (form.bp && form.bp.trim()) {
      const parsed = parseBP(form.bp);
      if (!parsed) {
        errs.bp = 'Enter as xxx/xxx (e.g. 120/70)';
      } else {
        if (parsed.sbp < 50 || parsed.sbp > 300) errs.bp = 'SBP must be 50–300';
        else if (parsed.dbp < 20 || parsed.dbp > 200) errs.bp = 'DBP must be 20–200';
      }
    }

    if (form.hr && form.hr.trim()) {
      const hr = parseInt(form.hr);
      if (isNaN(hr) || hr < 20 || hr > 300) errs.hr = 'HR must be 20–300';
    }

    const hasBP = form.bp && form.bp.trim() && !errs.bp;
    const hasHR = form.hr && form.hr.trim() && !errs.hr;
    const hasWt = metricMode
      ? (form.weight_kg && form.weight_kg.trim())
      : (form.weight_lbs && form.weight_lbs.trim());
    const hasHt = metricMode
      ? (form.height_cm && form.height_cm.trim())
      : (form.height_ft && form.height_ft.trim());

    if (!hasBP && !hasHR && !hasWt && !hasHt) {
      errs.general = 'Enter at least one value';
    }

    return errs;
  };

  // ── Build payload ──
  const buildPayload = () => {
    const bp = parseBP(form.bp);
    const parsedDate = parseDateInput(form.recorded_date);
    const parsedTime = form.recorded_time?.trim()
      ? parseTimeInput(form.recorded_time)
      : null;

    const payload = {
      recorded_date: parsedDate.iso,
      recorded_time: parsedTime ? parsedTime.storage : null,
      sbp: bp?.sbp ?? null,
      dbp: bp?.dbp ?? null,
      hr: form.hr ? parseInt(form.hr) : null,
    };

    if (metricMode) {
      payload.height_cm = form.height_cm ? parseFloat(form.height_cm) : null;
      payload.weight_kg = form.weight_kg ? parseFloat(form.weight_kg) : null;
    } else {
      payload.height_cm = ftInToCm(form.height_ft, form.height_in);
      payload.weight_kg = lbsToKg(form.weight_lbs);
    }

    return payload;
  };

  // ── Populate form for editing ──
  const populateForm = (vital) => {
    const { ft, in: inches } = cmToFtIn(vital.height_cm);
    setForm({
      recorded_date: isoToDisplay(vital.recorded_date),
      recorded_time: storageToTimeDisplay(vital.recorded_time),
      bp: formatBP(vital.sbp, vital.dbp) === '—' ? '' : formatBP(vital.sbp, vital.dbp),
      hr: vital.hr != null ? String(vital.hr) : '',
      weight_lbs: vital.weight_kg != null ? String(kgToLbs(vital.weight_kg)) : '',
      height_ft: ft,
      height_in: inches,
      weight_kg: vital.weight_kg != null ? String(vital.weight_kg) : '',
      height_cm: vital.height_cm != null ? String(vital.height_cm) : '',
    });
  };

  // ── Drawer open/close ──
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setConfirmDel(false);
    setDrawerOpen(true);
  };

  const openEdit = (vital) => {
    setEditingId(vital.id);
    populateForm(vital);
    setErrors({});
    setConfirmDel(false);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setConfirmDel(false);
  };

  // ── Use last height ──
  const handleUseLastHeight = () => {
    if (!lastHeightEntry) return;
    const { ft, in: inches } = cmToFtIn(lastHeightEntry.height_cm);
    if (metricMode) {
      handleChange('height_cm', String(lastHeightEntry.height_cm));
    } else {
      setForm(f => ({ ...f, height_ft: ft, height_in: inches }));
    }
  };

  // ── Save ──
  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await api.patch(`/patients/${resolvedId}/vitals/${editingId}`, payload);
      } else {
        await api.post(`/patients/${resolvedId}/vitals`, payload);
      }
      await onVitalsSaved();
      closeDrawer();
    } catch {
      setErrors({ general: 'Save failed — please try again' });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!editingId) return;
    setDeleting(true);
    try {
      await api.delete(`/patients/${resolvedId}/vitals/${editingId}`);
      await onVitalsSaved();
      closeDrawer();
    } catch {
      setErrors({ general: 'Delete failed — please try again' });
    } finally {
      setDeleting(false);
    }
  };

  // ── Audit line ──
  const editingVital = editingId ? vitals.find(v => v.id === editingId) : null;
  const auditLine = editingVital
    ? editingVital.modified_by
      ? `Modified by ${resolveUser(editingVital.modified_by)} on ${formatDateDisplay(editingVital.updated_at?.split('T')[0])}`
      : `Added by ${resolveUser(editingVital.created_by)} on ${formatDateDisplay(editingVital.created_at?.split('T')[0])}`
    : null;

  // ---------------------------------------------------------------------------
  // Drawer render
  // ---------------------------------------------------------------------------

  const renderDrawer = () => (
    <div style={{
      width: drawerOpen ? '320px' : '0px',
      opacity: drawerOpen ? 1 : 0,
      overflow: 'hidden',
      transition: 'width 0.25s ease, opacity 0.2s ease',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTop: `3px solid ${NAVY}`,
      borderRight: drawerOpen ? `1px solid ${BORDER}` : 'none',
      backgroundColor: SUBTLE,
    }}>
      {/* Drawer header */}
      <div style={s.drawerHeader}>
        <span style={s.drawerTitle}>
          {editingId ? 'Edit Vitals Entry' : 'Add Vitals Entry'}
        </span>
        <button style={s.drawerCloseBtn} onClick={closeDrawer} aria-label="Close">
          <IconX size={16} stroke={2} />
        </button>
      </div>

      {/* Drawer body */}
      <div style={s.drawerBody}>

        {/* Row 1: Date + Time */}
        <div style={s.formRow}>
          <div style={{ ...s.fieldGroup, flex: 1.4 }}>
            <label style={s.label}>Date <span style={s.required}>*</span></label>
            <DateInputField
              value={form.recorded_date}
              onChange={(display) => handleChange('recorded_date', display)}
              error={errors.recorded_date}
            />
          </div>
          <div style={{ ...s.fieldGroup, flex: 1 }}>
            <label style={s.label}>
              Time <span style={s.optional}>(optional)</span>
            </label>
            <input
              type="text"
              value={form.recorded_time}
              onChange={e => handleChange('recorded_time', e.target.value)}
              placeholder="e.g. 8:48 PM"
              style={{
                ...s.input,
                ...(errors.recorded_time ? s.inputError : {}),
              }}
            />
            {errors.recorded_time && (
              <div style={s.errorText}>{errors.recorded_time}</div>
            )}
          </div>
        </div>

        {/* Row 2: BP + HR */}
        <div style={s.formRow}>
          <div style={{ ...s.fieldGroup, flex: 1 }}>
            <label style={s.label}>
              BP <span style={s.hint}>(e.g. 128/72)</span>
            </label>
            <input
              type="text"
              value={form.bp}
              onChange={e => handleChange('bp', e.target.value)}
              style={{
                ...s.input,
                ...(errors.bp ? s.inputError : {}),
              }}
            />
            {errors.bp && <div style={s.errorText}>{errors.bp}</div>}
          </div>
          <div style={{ ...s.fieldGroup, flex: 1 }}>
            <label style={s.label}>
              HR <span style={s.hint}>(bpm)</span>
            </label>
            <input
              type="number"
              value={form.hr}
              onChange={e => handleChange('hr', e.target.value)}
              style={{
                ...s.input,
                ...(errors.hr ? s.inputError : {}),
              }}
            />
            {errors.hr && <div style={s.errorText}>{errors.hr}</div>}
          </div>
        </div>

        {/* Row 3: Height + Weight */}
        <div style={s.formRow}>
          {/* Height */}
          <div style={{ ...s.fieldGroup, flex: 1 }}>
            <div style={s.labelRow}>
              <label style={s.label}>
                Ht <span style={s.hint}>({metricMode ? 'cm' : 'ft/in'})</span>
              </label>
              {lastHeightEntry && !editingId && (
                <button style={s.useLastBtn} onClick={handleUseLastHeight}>
                  Use last ({cmToDisplay(lastHeightEntry.height_cm)})
                </button>
              )}
            </div>
            {metricMode ? (
              <input
                type="number"
                step="0.1"
                value={form.height_cm}
                onChange={e => handleChange('height_cm', e.target.value)}
                style={s.input}
              />
            ) : (
              <div style={s.ftInRow}>
                <input
                  type="number"
                  value={form.height_ft}
                  onChange={e => handleChange('height_ft', e.target.value)}
                  style={{ ...s.input, ...s.ftInput }}
                />
                <span style={s.ftLabel}>ft</span>
                <input
                  type="number"
                  step="0.1"
                  value={form.height_in}
                  onChange={e => handleChange('height_in', e.target.value)}
                  style={{ ...s.input, ...s.inInput }}
                />
                <span style={s.ftLabel}>in</span>
              </div>
            )}
          </div>

          {/* Weight */}
          <div style={{ ...s.fieldGroup, flex: 1 }}>
            <label style={s.label}>
              Wt <span style={s.hint}>({metricMode ? 'kg' : 'lbs'})</span>
            </label>
            {metricMode ? (
              <input
                type="number"
                step="0.1"
                value={form.weight_kg}
                onChange={e => handleChange('weight_kg', e.target.value)}
                style={s.input}
              />
            ) : (
              <input
                type="number"
                step="0.1"
                value={form.weight_lbs}
                onChange={e => handleChange('weight_lbs', e.target.value)}
                style={s.input}
              />
            )}
          </div>
        </div>

        {/* General error */}
        {errors.general && (
          <div style={s.errorText}>{errors.general}</div>
        )}

        {/* Audit line */}
        {auditLine && (
          <div style={s.auditLine}>{auditLine}</div>
        )}

      </div>

      {/* Drawer footer */}
      <div style={s.drawerFooter}>
        {confirmDel ? (
          <div style={s.confirmBox}>
            <div style={{
              fontSize: '13px',
              color: 'var(--color-severity-high)',
              fontWeight: 600,
              marginBottom: '10px',
              fontFamily: 'var(--font-family-base)',
            }}>
              This record will be permanently deleted.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Confirm delete'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}>
            <div>
              {editingId && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmDel(true)}
                  disabled={saving}
                >
                  Delete
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="sm" onClick={closeDrawer} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Table render
  // ---------------------------------------------------------------------------

  const renderTable = () => {
    const isEmpty = vitals.length === 0;
    return (
      <div style={s.tableWrapper}>
        <table style={s.table}>
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={s.th}>Date</th>
              <th style={s.th}>BP</th>
              <th style={s.th}>HR</th>
              <th style={s.th}>
                {metricMode ? 'Ht (cm)' : 'Ht'}
              </th>
              <th style={s.th}>
                {metricMode ? 'Wt (kg)' : 'Wt (lbs)'}
              </th>
              <th style={s.th}>BMI</th>
              <th style={{ ...s.th, width: '36px' }} />
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr style={s.tr}>
                <td style={s.emptyRow} colSpan={7}>
                  No vitals recorded yet.
                </td>
              </tr>
            ) : (
              vitals.map(v => {
                const isHovered = hoveredRow === v.id;
                const isEditing = editingId === v.id;

                return (
                  <tr
                    key={v.id}
                    style={{
                      ...s.tr,
                      backgroundColor: isEditing
                        ? 'var(--color-accent-wash)'
                        : isHovered
                          ? 'var(--color-accent-wash)'
                          : 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => setHoveredRow(v.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => isEditing ? closeDrawer() : openEdit(v)}
                  >
                    <td style={s.td}>
                      {formatDateTimeDisplay(v.recorded_date, v.recorded_time)}
                    </td>
                    <td style={s.td}>
                      <span style={s.cellInline}>
                        {formatBP(v.sbp, v.dbp)}
                        <BPIndicator sbp={v.sbp} dbp={v.dbp} />
                      </span>
                    </td>
                    <td style={s.td}>{v.hr ?? '—'}</td>
                    <td style={s.td}>
                      {metricMode ? cmToDisplayMetric(v.height_cm) : cmToDisplay(v.height_cm)}
                    </td>
                    <td style={s.td}>
                      {metricMode ? kgToDisplayMetric(v.weight_kg) : kgToDisplay(v.weight_kg)}
                    </td>
                    <td style={s.td}>
                      <span style={s.cellInline}>
                        {formatBMI(v.bmi)}
                        <BMIIndicator bmi={v.bmi} patient={patient} />
                      </span>
                    </td>
                    <td style={s.tdPencil}>
                      {isHovered && (
                        <IconPencil size={16} stroke={1.5} style={{ color: MUTED }} />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div style={s.root}>

      {/* Section title */}
      <div style={s.titleRow}>
        <span style={s.sectionHeading}>Vital Signs</span>
      </div>

      {/* Controls row: toggle left, add button right */}
      <div style={s.controlsRow}>
        <ToggleSwitch
          checked={metricMode}
          onChange={setMetricMode}
          labelLeft="US"
          labelRight="Metric"
        />
        {!drawerOpen && (
          <Button variant="primary" size="sm" onClick={openAdd}>
            + Add Vitals
          </Button>
        )}
      </div>

      {/* Legend row — always-on reference for the indicator glyphs */}
      <VitalsLegend />

      {/* Content area */}
      <div style={s.contentArea}>
        {renderDrawer()}
        <div style={s.tablePane}>
          {renderTable()}
        </div>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    fontFamily: 'var(--font-family-base)',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px 0 16px',
    flexShrink: 0,
  },
  sectionHeading: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: CHARCOAL,
    fontFamily: 'var(--font-family-base)',
  },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px 8px 16px',
    gap: '8px',
    flexShrink: 0,
  },
  contentArea: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    marginTop: '8px',
  },

  cellInline: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Drawer ──
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${BORDER}`,
    backgroundColor: 'var(--color-bg-card)',
    flexShrink: 0,
  },
  drawerTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: CHARCOAL,
    fontFamily: 'var(--font-family-base)',
  },
  drawerCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: MUTED,
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
    borderRadius: '4px',
  },
  drawerBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  drawerFooter: {
    padding: '12px 16px',
    borderTop: `1px solid ${BORDER}`,
    backgroundColor: 'var(--color-bg-card)',
    flexShrink: 0,
  },

  // ── Table ──
  tablePane: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 0 12px 0',
    minWidth: '400px',
  },
  tableWrapper: {
    overflowX: 'auto',
    maxWidth: '740px',
    border: `1px solid ${BORDER}`,
    borderTop: `3px solid ${NAVY}`,
    borderRadius: '0 0 var(--radius-sm) var(--radius-sm)',
    margin: '0 16px',
  },
  table: {
    width: '100%',
    maxWidth: '740px',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family-base)',
  },
  th: {
    textAlign: 'center',
    padding: '8px 12px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    color: MUTED,
    borderBottom: `1px solid ${BORDER}`,
    whiteSpace: 'nowrap',
    backgroundColor: SUBNAV_BG,
  },
  tr: {
    borderBottom: `1px solid ${BORDER}`,
    transition: 'background-color 0.15s ease',
  },
  td: {
    padding: '8px 12px',
    color: CHARCOAL,
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  emptyRow: {
    padding: '24px 12px',
    color: MUTED,
    fontStyle: 'italic',
    textAlign: 'center',
    fontFamily: 'var(--font-family-base)',
  },
  tdSelectedFirst: {
    borderLeft: `3px solid ${TEAL}`,
    paddingLeft: '9px',
  },
  tdPencil: {
    padding: '8px 8px',
    width: '36px',
    textAlign: 'center',
  },
  emptyState: {
    fontSize: 'var(--font-size-sm)',
    color: MUTED,
    fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic',
    padding: '48px 24px',
    textAlign: 'center',
  },

  // ── Form ──
  formRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: CHARCOAL,
    fontFamily: 'var(--font-family-base)',
  },
  required: {
    color: 'var(--color-severity-high)',
    marginLeft: '2px',
  },
  optional: {
    color: MUTED,
    fontWeight: 400,
    fontSize: '11px',
  },
  hint: {
    color: MUTED,
    fontWeight: 400,
    fontSize: '11px',
  },
  input: {
    fontSize: '13px',
    padding: '7px 10px',
    border: `1px solid ${BORDER}`,
    borderRadius: '4px',
    fontFamily: 'var(--font-family-base)',
    color: CHARCOAL,
    backgroundColor: 'var(--color-bg-card)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  inputError: {
    borderColor: 'var(--color-severity-high)',
  },
  ftInRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  ftInput: {
    width: '48px',
    flex: 'none',
  },
  inInput: {
    width: '52px',
    flex: 'none',
  },
  ftLabel: {
    fontSize: '11px',
    color: MUTED,
    fontFamily: 'var(--font-family-base)',
  },
  useLastBtn: {
    fontSize: '11px',
    color: TEAL,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    fontFamily: 'var(--font-family-base)',
    textDecoration: 'underline',
  },
  errorText: {
    fontSize: '11px',
    color: 'var(--color-severity-high)',
    fontFamily: 'var(--font-family-base)',
  },
  auditLine: {
    fontSize: '11px',
    color: MUTED,
    fontFamily: 'var(--font-family-base)',
    fontStyle: 'italic',
    paddingTop: '8px',
    borderTop: `1px solid ${BORDER}`,
    marginTop: '4px',
  },
  confirmBox: {
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid var(--color-severity-high)',
    backgroundColor: 'var(--color-severity-high-bg)',
  },
};